const _ = require("lodash");
const Font = require("../util/Font");
const CanvasText = require("../util/CanvasText");
const Operators = require("./Operators");
const RenderHelpers = require("./util/RenderHelpers");
const Math2D = require("../util/Math2D");

const DISABLE_FAST = false;

// We only use FastCanvasMath if the batex matches this pattern.
const fastPattern = new RegExp(
	"^((?:[^\\\\$]|\\n)+)$" + // Simple text with no math or commands.
	"|" +
	"^\\$\\s*([0-9\\.,]+)\\s*\\$$" + // A number in dollar signs.
		"|" +
		"^\\$\\s*(" +
		Operators.patternFragment +
		")\\s*\\$$" // An operator in dollar signs.
);
// Uses fastPattern to determine what text to draw, if any, given batex.
// Returns {text, padding}, where padding should be multiplied by fontSize.
// Returns null if this should not use fast rendering.
let parseBatex = function(batex) {
	if (typeof batex === "number") {
		return {text: String(batex), padding: 0};
	}
	const match = fastPattern.exec(batex);
	let text;
	let type = "text";
	let padding = 0;
	if (!match) {
		return null;
	} else if (match[1]) {
		text = match[1];
	} else if (match[2]) {
		text = match[2];
		type = "math";
	} else if (match[3]) {
		text = Operators.computeText(match[3]);
		type = "math";
		padding = 1 / 40;
	}
	// TODO: Making this {text, type, padding} broke things. How???
	return {text, parseType: type, padding};
};

// Returns null if fast draw could not be done.
const drawInRect = function(context, props) {
	let measure = props.computedFastMeasure;
	if (!measure) {
		// No measure value provided. Check if this is possible to render fast, and
		// if so do the measuring.
		const parsed = parseBatex(props.batex);
		if (parsed === null) {
			return null;
		}

		const wrapWidth =
			props.wrapWidth === undefined ? props.rect[2] : props.wrapWidth;
		const fontInfo = _.extend({}, Font.toInfo(context), props.fontInfo || {});

		context.save();
		context.textBaseline = "alphabetic";
		context.font = Font.fromInfo(fontInfo);
		measure = baseWrapAndMeasure(context, props, fontInfo, wrapWidth, parsed);
	} else {
		// Cached measure provided. Set up the context, but otherwise skip the
		// measuring and wrapping steps, which are already done.
		context.save();
		context.textBaseline = "alphabetic";
		context.font = Font.fromInfo(measure.fontInfo);
	}

	const fit = Math2D.fitInRect(
		[0, 0, measure.finalWidth, measure.finalHeight],
		props.rect,
		{
			scaleOff: props.scaleOff,
			scaleDownOnly: props.scaleDownOnly,
			distort: props.distort,
		}
	);
	context.translate(fit.translate[0], fit.translate[1]);
	context.scale(fit.scale[0], fit.scale[1]);

	baseDraw(context, props, measure, 0, 0);
	context.restore();

	return {
		measure: {
			width: measure.finalWidth * fit.scale[0],
			height: measure.finalHeight * fit.scale[1],
			// Note: does not match nonoptimized rendering, but we don't use it much
			props: props,
		},
		scale: fit.scale,
		rect: fit.fitRect,
		blanks: [],
		errors: [],
		fastMeasure: measure,
		unused: fit.unused,
	};
};

// Returns null if fast draw could not be done.
// noDraw determines whether any drawing is done. measure and render do
// identical things except render draws, so this is for code sharing.
const render = function(context, props, noDraw) {
	let measure = props.computedFastMeasure;
	if (!measure) {
		// No measure value provided. Check if this is possible to render fast, and
		// if so do the measuring.
		const parsed = parseBatex(props.batex);
		if (parsed === null) {
			return null;
		}

		const wrapWidth = props.wrapWidth;
		const fontInfo = _.extend({}, Font.toInfo(context), props.fontInfo || {});

		context.save();
		context.textBaseline = "alphabetic";
		context.font = Font.fromInfo(fontInfo);
		measure = baseWrapAndMeasure(context, props, fontInfo, wrapWidth, parsed);
	} else {
		// Cached measure provided. Set up the context, but otherwise skip the
		// measuring and wrapping steps, which are already done.
		context.save();
		context.textBaseline = "alphabetic";
		context.font = Font.fromInfo(measure.fontInfo);
	}

	if (!noDraw) {
		baseDraw(context, props, measure, props.x, props.y);
	}
	context.restore();

	return {
		measure: {
			width: measure.finalWidth,
			height: measure.finalHeight,
			// Note: does not match nonoptimized rendering, but we don't use it much
			props: props,
		},
		blanks: [],
		errors: [],
		fastMeasure: measure,
	};
};

// Returns null if fast draw could not be done.
const measureBatex = function(context, props) {
	return render(context, props, true);
};

let isFast = function(context, props) {
	return props.computedFastMeasure || parseBatex(props.batex) !== null;
};

const baseWrapAndMeasure = function(
	context,
	props,
	fontInfo,
	wrapWidth,
	{text, padding}
) {
	// Wrap text.
	const wrappedLines = wrapLines(context, fontInfo, wrapWidth, text);

	// Horizontal dimensions.
	const textPadding = padding * fontInfo.size;

	// Vertical dimensions.
	const linePadding = props.skipLinePadding
		? 0
		: RenderHelpers.getLinePadding(fontInfo);
	const lineHeight = fontInfo.size + 2 * linePadding;

	// Total dimensions.
	let finalWidth = _.max(wrappedLines.map(l => l.width)) + 2 * textPadding;
	const finalHeight = wrappedLines.length * lineHeight;
	if (props.centered || props.forceFullWidth) {
		finalWidth = Math.max(wrapWidth, finalWidth) || finalWidth;
	}

	return {
		wrappedLines,
		fontInfo,
		finalWidth,
		finalHeight,
		textPadding,
		linePadding,
		lineHeight,
	};
};

// measure is the return of baseWrapAndMeasure
const baseDraw = function(context, props, measure, x, y) {
	if (props.centered) {
		return baseDrawCentered(context, props, measure, x, y);
	}
	const {lineHeight, textPadding} = measure;
	y += lineHeight / 2;
	measure.wrappedLines.forEach(l => {
		CanvasText.drawMiddleText(context, l.text, x + textPadding, y);
		y += lineHeight;
	});
};

const baseDrawCentered = function(context, props, measure, x, y) {
	const {lineHeight, finalWidth} = measure;
	y += lineHeight / 2;
	measure.wrappedLines.forEach(l => {
		CanvasText.drawMiddleText(
			context,
			l.text,
			x + (finalWidth - l.width) / 2,
			y
		);
		y += lineHeight;
	});
};

// Returns array of {text, width} objects.
const wrapLines = function(context, fontInfo, wrapWidth, text) {
	let wrappedLines = text.split("\n");
	const spaceWidth = RenderHelpers.getSpaceWidth({context}, fontInfo);
	wrappedLines = _.flatten(
		wrappedLines.map(l => wrapOneLine(context, wrapWidth, spaceWidth, l))
	);
	return wrappedLines;
};

// Assumes text has no new line characters. Returns array of {text, width}.
const wrapOneLine = function(context, wrapWidth, spaceWidth, text) {
	let words = text.split(/\s+/);
	const lines = [];
	if (words[0] === "") {
		words = words.slice(1);
	}

	let currentLine = words.shift() || "";
	let lineWidth = context.measureText(currentLine).width;
	while (words.length) {
		const word = words.shift();
		const wordWidth = context.measureText(word).width;
		if (lineWidth + wordWidth + spaceWidth > wrapWidth) {
			lines.push({text: currentLine, width: lineWidth});
			currentLine = word;
			lineWidth = wordWidth;
		} else {
			currentLine += " " + word;
			lineWidth += wordWidth + spaceWidth;
		}
	}
	lines.push({text: currentLine, width: lineWidth});
	return lines;
};

// Replace functions indicating whether fast rendering is possible if disabling
// fast rendering.
if (DISABLE_FAST) {
	isFast = () => false;
	parseBatex = () => null;
}

module.exports = {
	parseBatex,
	drawInRect,
	render,
	measureBatex,
	isFast,
	baseWrapAndMeasure,
};
